import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { Bell, Save, Plus, X, Mail, CalendarClock } from 'lucide-react';
import axios from 'axios';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const ROLE_LABELS = {
  master_admin: 'Master Admin',
  company_owner: 'Bedriftseier',
  admin: 'Administrator',
  content_editor: 'Innholdsredaktør',
  mechanic: 'Mekaniker',
};

const RecipientBlock = ({ testPrefix, icon: Icon, title, description, users, block, onToggleUser, onAddEmail, onRemoveEmail }) => {
  const [newEmail, setNewEmail] = useState('');

  const addEmail = () => {
    const e = newEmail.trim().toLowerCase();
    if (!e) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) return;
    onAddEmail(e);
    setNewEmail('');
  };

  return (
    <Card className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 border-gray-700">
      <CardHeader>
        <CardTitle className="text-white flex items-center gap-2">
          <Icon className="w-5 h-5 text-red-500" /> {title}
        </CardTitle>
        <CardDescription className="text-gray-400">{description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div>
          <Label className="text-gray-300">Admin-brukere</Label>
          <div className="mt-2 space-y-2 max-h-64 overflow-y-auto pr-1">
            {users.map((u) => (
              <label
                key={u.id}
                className="flex items-center gap-3 rounded-lg bg-gray-900/60 border border-gray-700 px-3 py-2 cursor-pointer hover:border-red-600/50"
              >
                <Checkbox
                  data-testid={`${testPrefix}-user-${u.id}`}
                  checked={block.user_ids.includes(u.id)}
                  onCheckedChange={() => onToggleUser(u.id)}
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white truncate">{u.full_name || u.email}</div>
                  <div className="text-xs text-gray-500 truncate">{u.email}</div>
                </div>
                <Badge className="bg-gray-700 text-gray-300 text-xs">{ROLE_LABELS[u.role] || u.role}</Badge>
              </label>
            ))}
          </div>
        </div>

        <div>
          <Label className="text-gray-300">Ekstra e-postadresser</Label>
          <div className="flex flex-wrap gap-2 mt-2">
            {block.extra_emails.map((e) => (
              <Badge key={e} className="bg-red-600/20 text-red-300 border border-red-600/40 flex items-center gap-1 py-1">
                {e}
                <button
                  type="button"
                  data-testid={`${testPrefix}-remove-${e}`}
                  onClick={() => onRemoveEmail(e)}
                  className="ml-1 hover:text-white"
                >
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            ))}
            {block.extra_emails.length === 0 && (
              <span className="text-xs text-gray-500">Ingen ekstra adresser lagt til</span>
            )}
          </div>
          <div className="flex gap-2 mt-3">
            <Input
              type="email"
              placeholder="navn@epost.no"
              value={newEmail}
              data-testid={`${testPrefix}-email-input`}
              onChange={(e) => setNewEmail(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addEmail(); } }}
              className="bg-gray-900 border-gray-700 text-white"
            />
            <Button
              type="button"
              variant="outline"
              data-testid={`${testPrefix}-email-add`}
              onClick={addEmail}
              className="border-red-600 text-red-400 hover:bg-red-600/20"
            >
              <Plus className="w-4 h-4 mr-1" /> Legg til
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const AdminNotificationSettings = () => {
  const { toast } = useToast();
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);
  const [settings, setSettings] = useState({
    booking_notification: { user_ids: [], extra_emails: [] },
    daily_summary: { user_ids: [], extra_emails: [] },
  });

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    const load = async () => {
      try {
        const [sRes, uRes] = await Promise.all([
          axios.get(`${API}/notification-settings`, authHeaders),
          axios.get(`${API}/notification-settings/available-users`, authHeaders),
        ]);
        setSettings(sRes.data);
        setUsers(uRes.data || []);
      } catch (e) {
        toast({ title: 'Feil', description: 'Kunne ikke laste varsel-innstillinger', variant: 'destructive' });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const updateBlock = (type, updater) => {
    setSettings((prev) => ({ ...prev, [type]: updater(prev[type]) }));
  };

  const toggleUser = (type, userId) => {
    updateBlock(type, (b) => ({
      ...b,
      user_ids: b.user_ids.includes(userId)
        ? b.user_ids.filter((id) => id !== userId)
        : [...b.user_ids, userId],
    }));
  };

  const addEmail = (type, email) => {
    updateBlock(type, (b) => (b.extra_emails.includes(email) ? b : { ...b, extra_emails: [...b.extra_emails, email] }));
  };

  const removeEmail = (type, email) => {
    updateBlock(type, (b) => ({ ...b, extra_emails: b.extra_emails.filter((e) => e !== email) }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/notification-settings`, settings, authHeaders);
      toast({ title: 'Lagret ✅', description: 'Varsel-innstillinger er oppdatert' });
    } catch (e) {
      toast({ title: 'Feil', description: 'Kunne ikke lagre innstillinger', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-12 text-white text-xl">Laster varsel-innstillinger...</div>;
  }

  return (
    <div className="space-y-6" data-testid="notification-settings-page">
      <div>
        <h2 className="text-3xl font-bold mb-2">
          <span className="bg-gradient-to-r from-red-500 to-yellow-400 bg-clip-text text-transparent flex items-center gap-2">
            <Bell className="w-7 h-7 text-red-500" /> Varsel-innstillinger
          </span>
        </h2>
        <p className="text-gray-400">
          Velg hvilke admin-brukere og e-postadresser som skal motta varsler. Kundebekreftelsen går alltid til kundens egen e-post og påvirkes ikke her. Er en liste tom, sendes varselet til standardadressen (post@sand-tec.no).
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <RecipientBlock
          testPrefix="booking-notif"
          icon={Mail}
          title="Ny booking-varsel"
          description="Sendes umiddelbart når en ny booking opprettes."
          users={users}
          block={settings.booking_notification}
          onToggleUser={(id) => toggleUser('booking_notification', id)}
          onAddEmail={(e) => addEmail('booking_notification', e)}
          onRemoveEmail={(e) => removeEmail('booking_notification', e)}
        />
        <RecipientBlock
          testPrefix="daily-summary"
          icon={CalendarClock}
          title="Daglig oversikt"
          description="Daglig oppsummering av kommende bookinger (kl. 08:00)."
          users={users}
          block={settings.daily_summary}
          onToggleUser={(id) => toggleUser('daily_summary', id)}
          onAddEmail={(e) => addEmail('daily_summary', e)}
          onRemoveEmail={(e) => removeEmail('daily_summary', e)}
        />
      </div>

      <Button
        onClick={handleSave}
        disabled={saving}
        data-testid="notification-settings-save"
        className="w-full bg-red-600 hover:bg-red-700 text-white"
      >
        {saving ? <><span className="animate-spin mr-2">⏳</span>Lagrer...</> : <><Save className="w-4 h-4 mr-2" />Lagre varsel-innstillinger</>}
      </Button>
    </div>
  );
};

export default AdminNotificationSettings;
